import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PaymentCallback from "@/pages/artisan/PaymentCallback";
import CustomerPaymentCallback from "@/pages/CustomerPaymentCallback";
import { X } from "lucide-react";
import { PAYSTACK_CHECKOUT_EVENT } from "@/utils/paystackCheckout";

/**
 * PaystackCheckoutModal
 * ----------------------------------------------------------------------------
 * Renders Paystack's hosted checkout page inside a rounded, in-app dialog
 * instead of doing a full-page redirect. Mounted ONCE at the App root; any
 * caller opens it by firing the global `yorm:paystack-checkout` event
 * (see `utils/paystackCheckout.js -> openPaystackCheckout()`).
 *
 * How completion is detected:
 *   - Paystack redirects the iframe to our `callback_url` when the payment
 *     finishes (success OR failure). That callback lives on our own origin
 *     (e.g. `/artisan/payment/callback?reference=…`).
 *   - Cross-origin iframes throw on `contentWindow.location.href`. Once the
 *     iframe lands back on our origin the read succeeds and we know the
 *     payment step is done. We then close the modal and forward the parent
 *     window to that same callback path so the existing verify + receipt
 *     flow runs untouched.
 *   - The customer can also tap the X in the top-right at any time to abort.
 */
export default function PaystackCheckoutModal() {
  const [authorizationUrl, setAuthorizationUrl] = useState(null);
  const [resultUrl, setResultUrl] = useState(null);
  const iframeRef = useRef(null);
  const close = useCallback(() => { setAuthorizationUrl(null); setResultUrl(null); }, []);
  const result = useMemo(() => (resultUrl ? new URL(resultUrl) : null), [resultUrl]);

  // Listen for the global "open Paystack checkout" event so any caller in
  // the app can open the modal without threading state through providers.
  useEffect(() => {
    const handler = (e) => {
      const url = e?.detail?.authorizationUrl;
      if (!url) return;
      // ACK back to the dispatcher so it knows the modal handled it.
      try { e.detail?.ack?.(); } catch (_) {}
      setResultUrl(null);
      setAuthorizationUrl(url);
    };
    window.addEventListener(PAYSTACK_CHECKOUT_EVENT, handler);
    return () => window.removeEventListener(PAYSTACK_CHECKOUT_EVENT, handler);
  }, []);

  useEffect(() => {
    if (!authorizationUrl) return;
    const origin = window.location.origin;

    const interval = setInterval(() => {
      const iframe = iframeRef.current;
      if (!iframe) return;
      try {
        // Only succeeds once Paystack has redirected the iframe back to
        // our own origin — i.e. the customer finished the payment step.
        const href = iframe.contentWindow?.location?.href;
        if (href && href.startsWith(origin)) {
          clearInterval(interval);
          // Payment step finished: verify + show the result inside this same slide-up.
          setResultUrl(href);
        }
      } catch (_) {
        // Still cross-origin (still on checkout.paystack.com) — keep polling.
      }
    }, 400);

    return () => clearInterval(interval);
  }, [authorizationUrl]);

  if (!authorizationUrl) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] pointer-events-auto bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center animate-in fade-in duration-200"
      data-testid="paystack-checkout-modal"
      onClick={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      <div
        className={`relative bg-white rounded-t-[24px] sm:rounded-2xl shadow-2xl w-full sm:max-w-md ${result ? "h-auto max-h-[80dvh]" : "h-[92dvh] sm:h-[86dvh] max-h-[820px]"} overflow-hidden flex flex-col animate-in slide-in-from-bottom duration-300 pb-[env(safe-area-inset-bottom)]`}
        data-testid="paystack-checkout-sheet"
      >
        <div className="flex items-center justify-between px-4 pt-2 pb-2 border-b border-gray-100 shrink-0">
          <span className="w-10" />
          <div className="flex flex-col items-center">
            <span className="w-9 h-1.5 rounded-full bg-gray-300 mb-1.5 sm:hidden" />
            <span className="text-[13px] font-semibold text-gray-600">{result ? "Payment result" : "Secure payment"}</span>
          </div>
          <button
            type="button"
            onClick={close}
            aria-label="Close payment"
            data-testid="paystack-checkout-close-btn"
            className="w-10 h-10 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-600 transition-colors"
          >
            <X size={20} />
          </button>
        </div>
        {result ? (
          <div className="flex-1 overflow-y-auto" data-testid="paystack-result-panel">
            {result.pathname.startsWith("/customer/")
              ? <CustomerPaymentCallback embedded params={result.searchParams} onDone={close} />
              : <PaymentCallback embedded params={result.searchParams} onDone={close} />}
          </div>
        ) : (
          <iframe
            ref={iframeRef}
            src={authorizationUrl}
            title="Paystack Checkout"
            allow="payment; clipboard-read; clipboard-write"
            className="w-full flex-1 border-0"
            data-testid="paystack-checkout-iframe"
          />
        )}
      </div>
    </div>
  );
}
